import { AxiosError, AxiosResponse, CancelToken } from "axios";
import { Action } from "redux";
import api from "src/spintr/SpintrApi";

import {
    createConversation as createConversationApi
} from "../api";
import { IGetConversationsParams } from "../conversation-types";
import { ConversationActionTypes } from "./conversation-action-types";

const baseUrl = "/api/v1/conversations";

export interface IQueryConversationsAction extends Action {
    meta: IGetConversationsParams;
    payload: Promise<AxiosResponse<Spintr.IConversationResponse>>;
    type: ConversationActionTypes.QueryConversations;
}

export interface IQueryConversationsFulfilledAction extends Action {
    meta: IGetConversationsParams;
    payload: AxiosResponse<Spintr.IConversationResponse>;
    type: ConversationActionTypes.QueryConversationsFulfilled;
}

export interface IQueryConversationsPendingAction extends Action {
    meta: IGetConversationsParams;
    payload: Promise<AxiosResponse<Spintr.IConversationResponse>>;
    type: ConversationActionTypes.QueryConversationsPending;
}

export interface IQueryConversationsRejectedAction extends Action {
    meta: IGetConversationsParams;
    payload: AxiosError;
    type: ConversationActionTypes.QueryConversationsRejected;
}

export type QueryConversationsAction =
      IQueryConversationsAction
    | IQueryConversationsFulfilledAction
    | IQueryConversationsPendingAction
    | IQueryConversationsRejectedAction;

export type QueryConversationsHandler =
    (params: IGetConversationsParams, cancelToken?: CancelToken) => IQueryConversationsAction;
export const queryConversations: QueryConversationsHandler = (params, cancelToken) => ({
    meta: params,
    payload: api.get(baseUrl + "/combined", {
        params,
        cancelToken,
    }),
    type: ConversationActionTypes.QueryConversations,
});

export interface IGetConversationAction extends Action {
    payload: Promise<AxiosResponse<Spintr.IConversation>>;
    type: ConversationActionTypes.GetConversation;
}

export interface IGetConversationFulfilledAction extends Action {
    payload: AxiosResponse<Spintr.IConversation>;
    type: ConversationActionTypes.GetConversationFulfilled;
}

export interface IGetConversationPendingAction extends Action {
    payload: Promise<AxiosResponse<Spintr.IConversation>>;
    type: ConversationActionTypes.GetConversationPending;
}

export interface IGetConversationRejectedAction extends Action {
    payload: AxiosError;
    type: ConversationActionTypes.GetConversationRejected;
}

export type GetConversationAction = 
      IGetConversationAction
    | IGetConversationFulfilledAction
    | IGetConversationPendingAction
    | IGetConversationRejectedAction;

export type GetConversationHandler = (id: number) => IGetConversationAction;
export const getConversation: GetConversationHandler = (id : number) => ({
    payload: api.get(baseUrl + "/" + id),
    type: ConversationActionTypes.GetConversation,
});

export interface ISetTabStateAction extends Action {
    id: number;
    minimized: boolean;
    open: boolean;
    type: ConversationActionTypes.SetTabState;
}

export type SetTabStateHandler =
    (id: number, open?: boolean, minimized?: boolean) => ISetTabStateAction;
export const setTabState: SetTabStateHandler = (id, open = true, minimized = true) => ({
    id, minimized, open, type: ConversationActionTypes.SetTabState,
});


export interface ICreateConversationAction extends Action {
    meta: Spintr.IConversation;
    payload: Promise<AxiosResponse<Spintr.IConversation>>;
    type: ConversationActionTypes.CreateConversation;
}

export interface ICreateConversationFulfilledAction extends Action {
    meta: Spintr.IConversation;
    payload: AxiosResponse<Spintr.IConversation>;
    type: ConversationActionTypes.CreateConversationFulfilled;
}

export interface ICreateConversationPendingAction extends Action {
    meta: Spintr.IConversation;
    payload: Promise<AxiosResponse<Spintr.IConversation>>;
    type: ConversationActionTypes.CreateConversationPeding;
}

export interface ICreateConversationRejectedAction extends Action {
    meta: Spintr.IConversation;
    payload: AxiosError<Spintr.IConversation>;
    type: ConversationActionTypes.CreateConversationRejected;
}

export type BoundCreateConversationHandler =
    (conversation: Spintr.IConversation) => Promise<AxiosResponse<Spintr.IConversation> | AxiosError<never>>;

export type CreateConversationHandler =
    (conversation: Spintr.IConversation) => ICreateConversationAction;
export const createConversation: CreateConversationHandler =
    (conversation) => ({
        meta: conversation,
        payload: createConversationApi(
            conversation.participants,
            conversation.message,
        ),
        type: ConversationActionTypes.CreateConversation,
    });

export type CreateConversationAction =
      ICreateConversationAction
    | ICreateConversationFulfilledAction
    | ICreateConversationPendingAction
    | ICreateConversationRejectedAction;

export interface IMarkAsReadMeta {
    conversationId: number;
    prevUnread?: number;
    messageId?: number;
    isSystem?: boolean;
}

export interface IMarkAsReadAction extends Action {
    meta: IMarkAsReadMeta;
    payload: Promise<AxiosResponse<never>>;
    type: ConversationActionTypes.MarkAsRead;
}

export interface IMarkAsReadPendingAction extends Action {
    meta: IMarkAsReadMeta;
    payload: Promise<AxiosResponse<never>>;
    type: ConversationActionTypes.MarkAsReadPending;
}

export interface IMarkAsReadFulfilledAction extends Action {
    meta: IMarkAsReadMeta;
    payload: AxiosResponse<never>;
    type: ConversationActionTypes.MarkAsReadFulfilled;
}

export interface IMarkAsReadRejectedAction extends Action {
    meta: IMarkAsReadMeta;
    payload: AxiosError<never>;
    type: ConversationActionTypes.MarkAsReadRejected;
}

export type MarkAsReadHandler =
    (conversationId: number, prevUnread?: number, messageId?: number, isSystem?: boolean) => IMarkAsReadAction;

export const markAsRead: MarkAsReadHandler = (conversationId, prevUnread, messageId?: number, isSystem?: boolean) => ({
    meta: {
        conversationId, prevUnread,
    },
    payload: api.put(
        baseUrl + "/" + conversationId + "/read",
        messageId != null || isSystem ?
            {
                messageId,
                isSystem
            } :
            {}
    ),
    type: ConversationActionTypes.MarkAsRead,
});

export type MarkAsReadAction =
      IMarkAsReadAction
    | IMarkAsReadFulfilledAction
    | IMarkAsReadPendingAction
    | IMarkAsReadRejectedAction;

export interface IAddRemoteConversationAction extends Action {
    conversation: Spintr.IConversation;
    type: ConversationActionTypes.AddRemoteConversation;
}

export type AddRemoteConversationHandler =
    (conversation: Spintr.IConversation) => IAddRemoteConversationAction;

export const addRemoteConversation: AddRemoteConversationHandler =
    (conversation) => ({
        conversation,
        type: ConversationActionTypes.AddRemoteConversation,
    });

export interface ILastReadReceivedAction extends Action {
    conversationId: number;
    messageId: number;
    userId: number;
    type: ConversationActionTypes.LastReadReceived;
}

export type LastReadReceivedHandler = (
    conversationId: number,
    messageId: number,
    userId: number,
) => ILastReadReceivedAction;

export const lastReadReceived: LastReadReceivedHandler =
    (conversationId, messageId, userId) => ({
        conversationId,
        messageId,
        type: ConversationActionTypes.LastReadReceived,
        userId,
    });

export interface IAddIsTypingAction extends Action {
    conversationId: number;
    userId: number;
    type: ConversationActionTypes.AddIsTyping;
}

export type AddIsTypingHandler = (
    conversationId: number,
    userId: number,
) => IAddIsTypingAction;

export const addIsTyping: AddIsTypingHandler =
    (conversationId, userId) => ({
        conversationId,
        type: ConversationActionTypes.AddIsTyping,
        userId,
    });

export interface IRemoveIsTypingAction extends Action {
    conversationId: number;
    userId: number;
    type: ConversationActionTypes.RemoveIsTyping;
}

export type RemoveIsTypingHandler = (
    conversationId: number,
    userId: number,
) => IRemoveIsTypingAction;

export const removeIsTyping: RemoveIsTypingHandler =
    (conversationId, userId) => ({
        conversationId,
        type: ConversationActionTypes.RemoveIsTyping,
        userId,
    });

export interface IUpdateGroupActivityAction extends Action {
    meta: number;
    payload: Promise<AxiosResponse<any>>;
    type: ConversationActionTypes.UpdateGroupActivity;
}

export type UpdateGroupActivityHandler = (groupId: number) => IUpdateGroupActivityAction;

export const updateGroupActivity: UpdateGroupActivityHandler = (groupId) => ({
    meta: groupId,
    payload: api.put(`/api/v1/groups/${groupId}/visited`),
    type: ConversationActionTypes.UpdateGroupActivity,
});

export interface IUpdateGroupActivityActionPending extends Action {
    meta: number;
    payload: Promise<AxiosResponse<any>>;
    type: ConversationActionTypes.UpdateGroupActivityPending;
}

export interface ISetUnreadCountPersonConversations extends Action {
    meta: number;
    type: ConversationActionTypes.SetUnreadCountPersonConversations
}

export type SetUnreadCountPersonConversations = (meta: number) => ISetUnreadCountPersonConversations;

export const setUnreadCountPersonConversations : SetUnreadCountPersonConversations =
    (value: number) => ({
        meta: value,
        type: ConversationActionTypes.SetUnreadCountPersonConversations
    });

export interface ISetUnreadCountGroupConversations extends Action {
    meta: number;
    type: ConversationActionTypes.SetUnreadCountGroupConversations
}

export type SetUnreadCountGroupConversations = (meta: number) => ISetUnreadCountGroupConversations;

export const setUnreadCountGroupConversations : SetUnreadCountGroupConversations =
    (value: number) => ({
        meta: value,
        type: ConversationActionTypes.SetUnreadCountGroupConversations,
    });

export interface IUpdateConversation extends Action {
    conversation: Spintr.IConversation,
    type: ConversationActionTypes.UpdateConversation;
}

export type UpdateConversationHandler = (
    conversation: Spintr.IConversation,
) => IUpdateConversation;

export const updateConversation: UpdateConversationHandler =
    (conversation: Spintr.IConversation) => ({
        conversation,
        type: ConversationActionTypes.UpdateConversation
    });

export interface ISetComposerReplyMessage extends Action {
    message: Spintr.IChatMessage,
    conversationId: number;
    type: ConversationActionTypes.SetComposerReplyMessage;
}

export type SetComposerReplyMessageHandler = (
    message: Spintr.IChatMessage,
    conversationId: number
) => ISetComposerReplyMessage;

export const setComposerReplyMessage: SetComposerReplyMessageHandler =
    (message: Spintr.IChatMessage, conversationId: number) => ({
        message: message,
        conversationId: conversationId,
        type: ConversationActionTypes.SetComposerReplyMessage
    });

export interface ISetComposerEditMessage extends Action {
    message: Spintr.IChatMessage,
    conversationId: number;
    type: ConversationActionTypes.SetComposerEditMessage;
}

export type SetComposerEditMessageHandler = (
    message: Spintr.IChatMessage,
    conversationId: number
) => ISetComposerEditMessage;

export const setComposerEditMessage: SetComposerEditMessageHandler =
(message: Spintr.IChatMessage, conversationId: number) => ({
        message: message,
        conversationId: conversationId,
        type: ConversationActionTypes.SetComposerEditMessage
    });

export type UpdateGroupActivityAction = IUpdateGroupActivityAction | IUpdateGroupActivityActionPending;

export type ConversationAction = 
    QueryConversationsAction
    | GetConversationAction
    | ISetTabStateAction
    | CreateConversationAction
    | MarkAsReadAction
    | IAddRemoteConversationAction
    | ILastReadReceivedAction
    | IAddIsTypingAction
    | IRemoveIsTypingAction
    | UpdateGroupActivityAction
    | ISetUnreadCountPersonConversations
    | ISetUnreadCountGroupConversations
    | ISetComposerReplyMessage
    | ISetComposerEditMessage
    | IUpdateConversation;